import {
  AfterContentInit,
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  OnChanges,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { Product } from '@scalefast/core';
import { AppSplideProductGalleryComponent } from '../splide-product-gallery/splide-product-gallery.component';
import { GenericLogger } from '@scalefast/ecommerce-core';

export type ProductGallerySubset = Pick<Product, 'gallery' | 'id'>;

/**
 * Product Gallery Component
 * This component implements the PDP product gallery
 * Under the hood it wraps splide-product-gallery component that needs special rendering handling to integrate with Splide
 * library and support SSR.
 */
@Component({
  standalone: true,
  selector: 'app-product-gallery',

  template: `<ng-container #outlet />
    <ng-template #content>
      <app-splide-product-gallery
        [product]="product()"
        [showLowestPriceTag]="showLowestPriceTag()"
        [defaultAltText]="defaultAltText()"
      />
    </ng-template>`,
  imports: [AppSplideProductGalleryComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppProductGalleryComponent implements OnChanges, AfterViewInit {
  product = input.required<ProductGallerySubset>();
  showLowestPriceTag = input(false);
  defaultAltText = input('');

  #logger = inject(GenericLogger);

  #rendered = false;

  @ViewChild('outlet', { read: ViewContainerRef }) outletRef?: ViewContainerRef;
  @ViewChild('content', { read: TemplateRef }) contentRef?: TemplateRef<any>;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.product && this.outletRef && this.contentRef) {
      this.#reRender();
    }
  }
  ngAfterViewInit(): void {
    this.#reRender();
  }

  /**
   * Hack to force re-rendering the gallery component when the gallery items change, we wan't to avoid angular trying to update the
   * underlying splide component in place as we need to re-create it and Angular doesn't understand the changes made by splide.
   */
  #reRender() {
    if (this.outletRef && this.contentRef) {
      this.outletRef.clear();
      this.outletRef.createEmbeddedView(this.contentRef);
      if (!this.#rendered) {
        this.#rendered = true;
        this.#logger.debug('Splide product gallery view initial render');
      } else {
        this.#logger.debug('Splide product gallery view re-rendered');
      }
    } else {
      this.#logger.debug('outletRef or contentRef not available yet');
    }
  }
}
